import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import type { OpenAPI } from "simplydo/interfaces";

import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { useConnectedEntities } from "./ConnectionsManager";

type GetConnectionsRequest =
  OpenAPI.GET<"/connections/{entity}/{service}/{context_id}">;
type OnRemoveConnectionResponse =
  OpenAPI.DELETE<"/connections/{entity}/{service}/{context_id}/{entity_id}">["response"];
type OnConnectCallback = (refreshCallback?: () => void) => void;

type ConnectedEntity = GetConnectionsRequest["response"]["entities"][number];

type UserConnectionManagerProps = {
  entity: string;
  entityId: string;
  serviceProvider: "microsoft";
  service: string;
  contextId: string;
  readOnly?: boolean;
  onConnect?: OnConnectCallback;
  onDisconnect?: (
    entityId: string,
    response: OnRemoveConnectionResponse,
  ) => void;
  onConnectionError?: (errorMessage: string) => void;
  renderConnected: (args: {
    connectedEntities: ConnectedEntity[];
    removeConnection: (entityId: string) => void;
    isDisconnectingEntityId: string | undefined;
  }) => ReactNode;
  renderDisconnected: (args: {
    connect: () => void;
    loading: boolean;
    isConnecting: boolean;
    errorMessage: string;
    readOnly: boolean;
  }) => ReactNode;
};

const UserConnectionManager = ({
  entity,
  entityId,
  serviceProvider,
  service,
  contextId,
  readOnly = false,
  onConnect = () => {},
  onDisconnect = () => {},
  onConnectionError = () => {},
  renderConnected,
  renderDisconnected,
}: UserConnectionManagerProps) => {
  const user = useCurrentUser();
  const [isConnecting, setIsConnecting] = useState(false);

  const {
    loading,
    errorMessage,
    connectedEntities,
    removeConnection,
    isDisconnectingEntityId,
  } = useConnectedEntities(
    entity,
    service,
    contextId,
    onConnect,
    onDisconnect,
    onConnectionError,
  );

  const currentEntityIsConnected = useMemo(
    () =>
      connectedEntities.some(
        (connectedEntity) => connectedEntity._id === entityId,
      ),
    [connectedEntities, entityId],
  );

  useEffect(() => {
    if (currentEntityIsConnected || errorMessage) {
      setIsConnecting(false);
    }
  }, [currentEntityIsConnected, errorMessage]);

  const connect = useCallback(() => {
    if (readOnly || !user) return;
    setIsConnecting(true);
    util.connectOAuth(
      serviceProvider,
      user,
      entity,
      entityId,
      contextId,
      service,
    );
  }, [contextId, entity, entityId, readOnly, service, serviceProvider, user]);

  if (currentEntityIsConnected) {
    return renderConnected({
      connectedEntities,
      removeConnection,
      isDisconnectingEntityId,
    });
  }

  return renderDisconnected({
    connect,
    loading,
    isConnecting,
    errorMessage,
    readOnly,
  });
};

export default UserConnectionManager;
